import { useEffect, useRef } from 'react'

type MenuItem = 
  | {
      label: string
      icon: React.ReactNode
      onClick: () => void
    }
  | {
      type: 'separator'
    }

interface ImageContextMenuProps {
  x: number
  y: number
  itemIndex: number
  onClose: () => void
  onView: () => void
  onDownload: () => void
  onSendToBack: () => void
  onSendToFront: () => void
  onRotate: () => void
  onLink?: () => void
  onSetAsCover?: () => void
}

/**
 * 图片右键菜单组件
 */
export const ImageContextMenu = ({
  x,
  y,
  itemIndex: _itemIndex,
  onClose,
  onView,
  onDownload,
  onSendToBack,
  onSendToFront,
  onRotate,
  onLink,
  onSetAsCover,
}: ImageContextMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null)

  // 点击其他地方关闭菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    // 延迟添加事件监听，避免立即触发
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true)
      document.addEventListener('contextmenu', handleClickOutside, true)
    }, 0)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('click', handleClickOutside, true)
      document.removeEventListener('contextmenu', handleClickOutside, true)
    }
  }, [onClose])

  // 确保菜单在视口内
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let adjustedX = x
      let adjustedY = y

      // 如果菜单超出右边界，调整到左侧
      if (rect.right > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 10
      }

      // 如果菜单超出下边界，调整到上方
      if (rect.bottom > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 10
      }

      // 确保不超出左边界和上边界
      adjustedX = Math.max(10, adjustedX)
      adjustedY = Math.max(10, adjustedY)

      menuRef.current.style.left = `${adjustedX}px`
      menuRef.current.style.top = `${adjustedY}px`
    }
  }, [x, y])

  const menuItems = [
    {
      label: '查看图片',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      ),
      onClick: () => {
        onView()
        onClose()
      },
    },
    {
      label: '下载',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
      ),
      onClick: () => {
        onDownload()
        onClose()
      },
    },
    {
      label: '送到最底层',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M2 12h20M12 2v20M7 7l5-5 5 5M7 17l5 5 5-5"></path>
        </svg>
      ),
      onClick: () => {
        onSendToBack()
        onClose()
      },
    },
    {
      label: '送到最高层',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M2 12h20M12 2v20M17 7l-5-5-5 5M17 17l-5 5-5-5"></path>
        </svg>
      ),
      onClick: () => {
        onSendToFront()
        onClose()
      },
    },
    {
      label: '旋转',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"></path>
        </svg>
      ),
      onClick: () => {
        onRotate()
        onClose()
      },
    },
    {
      label: '任意门 (链接)',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
        </svg>
      ),
      onClick: () => {
        if (onLink) {
          onLink()
        }
        onClose()
      },
    },
  ]

  // 如果有 onSetAsCover，添加分隔线和"设置为封面"菜单项
  const finalMenuItems: MenuItem[] = onSetAsCover
    ? [
        ...menuItems,
        { type: 'separator' as const },
        {
          label: '设置为封面',
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
          ),
          onClick: () => {
            onSetAsCover()
            onClose()
          },
        },
      ]
    : menuItems

  return (
    <div
      ref={menuRef}
      className="fixed z-[10000] bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]"
      style={{
        left: `${x}px`,
        top: `${y}px`,
      }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {finalMenuItems.map((item, index) => {
        if ('type' in item && item.type === 'separator') {
          return <div key={index} className="my-1 border-t border-gray-200" />
        }
        if ('label' in item && 'onClick' in item) {
          return (
            <div
              key={index}
              className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors text-sm text-gray-700"
              onClick={item.onClick}
            >
              <span className="flex-shrink-0 text-gray-600">{item.icon}</span>
              <span>{item.label}</span>
            </div>
          )
        }
        return null
      })}
    </div>
  )
}
